import * as React from "react"
import { ChevronLeft, LogOut, MessageSquare, UserPlus, X } from "lucide-react"
import { useNavigate, useParams } from "react-router"

import { UserBlobatar } from "@/components/chat/blobatar"
import { UserAvatar } from "@/components/chat/user-avatar"
import { UserPicker } from "@/components/chat/user-picker"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { Spinner } from "@/components/ui/spinner"
import { roomDisplayName } from "@/chat/format"
import type { User } from "@/chat/protocol"
import { useChat } from "@/chat/store"

export function RoomInfo() {
  const { id = "" } = useParams()
  const navigate = useNavigate()
  const { rooms, members, messages, user, openDirect, invite, leave } =
    useChat()

  const [opening, setOpening] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [inviting, setInviting] = React.useState(false)

  const room = rooms.find((r) => r.id === id)
  const here = members[id] ?? []
  const loaded = messages[id] ?? []
  const selfId = user?.id ?? null

  const isDirect = room?.kind === "direct"
  const title = room ? roomDisplayName(room, selfId) : id

  // Every room is its members; presence only says who has it open right now.
  const people: User[] = React.useMemo(() => room?.participants ?? [], [room])
  const hereIds = new Set(here.map((m) => m.id))
  const memberIds = React.useMemo(
    () => new Set(people.map((p) => p.id)),
    [people]
  )

  function leaveRoom() {
    if (!room) return
    const last = people.length <= 1
    const question = last
      ? "Вы последний участник — комната и её история будут удалены. Выйти?"
      : `Покинуть комнату «${title}»? Вернуться можно только по приглашению.`
    if (!window.confirm(question)) return

    leave(room.id)
    navigate("/")
  }

  async function message(target: User) {
    if (opening) return

    setOpening(target.id)
    setError(null)

    try {
      const direct = await openDirect(target.id)
      navigate(`/r/${direct.id}`)
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Не удалось открыть чат."
      )
      setOpening(null)
    }
  }

  return (
    <>
      <header className="flex flex-none items-center gap-2 border-b px-2 py-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(`/r/${id}`)}
          aria-label="Назад к беседе"
        >
          <ChevronLeft className="size-5" />
        </Button>
        <span className="truncate text-sm font-medium">
          {isDirect ? "Личный чат" : "Информация о комнате"}
        </span>
      </header>

      {/* A settings page reads badly stretched across a desktop pane. */}
      <div className="no-scrollbar flex-1 overflow-y-auto overscroll-contain [&>*]:mx-auto [&>*]:w-full [&>*]:max-w-2xl">
        <div className="flex flex-col items-center px-6 py-6 text-center">
          <UserAvatar name={title} className="size-20 text-xl" accent />

          <h1 className="mt-4 text-xl font-semibold tracking-tight">{title}</h1>

          <div className="mt-3 flex flex-wrap justify-center gap-1.5">
            {isDirect ? (
              <Badge variant="secondary">Только вы двое</Badge>
            ) : (
              <>
                <Badge variant="secondary">Участников: {people.length}</Badge>
                <Badge variant="outline">Сейчас здесь: {here.length}</Badge>
              </>
            )}
          </div>

          <Button className="mt-5" onClick={() => navigate(`/r/${id}`)}>
            Открыть беседу
          </Button>
        </div>

        <Separator />

        <div className="flex flex-col gap-6 px-6 py-6">
          {room?.topic && !isDirect && (
            <section>
              <h2 className="text-xs font-medium tracking-wider text-muted-foreground uppercase">
                Тема
              </h2>
              <p className="mt-2 text-sm leading-relaxed">{room.topic}</p>
            </section>
          )}

          <section>
            <div className="flex items-center justify-between">
              <h2 className="text-xs font-medium tracking-wider text-muted-foreground uppercase">
                Участники
              </h2>
              {!isDirect && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setInviting((v) => !v)}
                  aria-label={inviting ? "Отменить" : "Добавить участника"}
                >
                  {inviting ? (
                    <X className="size-3.5" />
                  ) : (
                    <UserPlus className="size-3.5" />
                  )}
                  {inviting ? "Отмена" : "Добавить"}
                </Button>
              )}
            </div>

            {inviting && (
              <div className="mt-2">
                <UserPicker
                  placeholder="Логин участника"
                  exclude={memberIds}
                  autoFocus
                  onPick={(person) => {
                    invite(id, person.id)
                    setInviting(false)
                  }}
                />
              </div>
            )}

            {error && <p className="mt-2 text-xs text-destructive">{error}</p>}

            {people.length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">Загрузка…</p>
            ) : (
              <ul className="mt-3 flex flex-col gap-2">
                {people.map((person) => {
                  const isSelf = person.id === selfId
                  return (
                    <li key={person.id} className="flex items-center gap-2.5">
                      <UserBlobatar name={person.name} className="size-7" />
                      <span className="truncate text-sm">{person.name}</span>

                      {!isSelf && hereIds.has(person.id) && (
                        <Badge variant="outline">в сети</Badge>
                      )}

                      {isSelf ? (
                        <Badge variant="secondary" className="ml-auto">
                          вы
                        </Badge>
                      ) : (
                        !isDirect && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="ml-auto size-7"
                            aria-label={`Написать ${person.name}`}
                            disabled={opening !== null}
                            onClick={() => void message(person)}
                          >
                            {opening === person.id ? (
                              <Spinner className="size-3.5" />
                            ) : (
                              <MessageSquare className="size-3.5" />
                            )}
                          </Button>
                        )
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
          </section>

          <section>
            <h2 className="text-xs font-medium tracking-wider text-muted-foreground uppercase">
              История
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {loaded.length === 0
                ? "Пока ничего не загружено."
                : `Загружено сообщений: ${loaded.length}.`}
            </p>
          </section>

          {!isDirect && room && (
            <section>
              <Button
                variant="outline"
                className="w-full text-destructive hover:text-destructive"
                onClick={leaveRoom}
              >
                <LogOut className="size-4" />
                Покинуть комнату
              </Button>
            </section>
          )}
        </div>
      </div>
    </>
  )
}
